import { TicketPriority } from "@jugl-web/rest-api/tickets/models/common-types";
import { useTranslations } from "@jugl-web/utils";
import { FC, ReactNode, SVGProps, useCallback, useMemo } from "react";
import { ReactComponent as HighIcon } from "./assets/high.svg";
import { ReactComponent as LowIcon } from "./assets/low.svg";
import { ReactComponent as MediumIcon } from "./assets/medium.svg";
import { ReactComponent as NoneIcon } from "./assets/none.svg";

export interface TicketPriorityDetails {
  id: TicketPriority;
  label: string;
  color: string;
  icon: ReactNode;
}

export const TICKET_PRIORITY_IDS: TicketPriority[] = [
  "none",
  "low",
  "medium",
  "high",
];

export const TICKET_PRIORITY_COLORS: Record<TicketPriority, string> = {
  none: "#BDBDBD",
  low: "#50BA4D",
  medium: "#FF8F27",
  high: "#EA5353",
};

export const TICKET_PRIORITY_ICONS: Record<
  TicketPriority,
  FC<SVGProps<SVGSVGElement>>
> = {
  none: NoneIcon,
  low: LowIcon,
  medium: MediumIcon,
  high: HighIcon,
};

export const useTicketPriorities = () => {
  const { t } = useTranslations();

  const priorityById = useMemo<Record<TicketPriority, TicketPriorityDetails>>(
    () => ({
      none: {
        id: "none",
        label: t({
          id: "client-support-page.ticket-priority-none-label",
          defaultMessage: "None",
        }),
        color: TICKET_PRIORITY_COLORS.none,
        icon: <NoneIcon />,
      },
      low: {
        id: "low",
        label: t({
          id: "client-support-page.ticket-priority-low-label",
          defaultMessage: "Low",
        }),
        color: TICKET_PRIORITY_COLORS.low,
        icon: <LowIcon />,
      },
      medium: {
        id: "medium",
        label: t({
          id: "client-support-page.ticket-priority-medium-label",
          defaultMessage: "Medium",
        }),
        color: TICKET_PRIORITY_COLORS.medium,
        icon: <MediumIcon />,
      },
      high: {
        id: "high",
        label: t({
          id: "client-support-page.ticket-priority-high-label",
          defaultMessage: "High",
        }),
        color: TICKET_PRIORITY_COLORS.high,
        icon: <HighIcon />,
      },
    }),
    [t]
  );

  const getPriorityById = useCallback(
    (priority: TicketPriority): TicketPriorityDetails => priorityById[priority],
    [priorityById]
  );

  const priorities = useMemo<TicketPriorityDetails[]>(
    () => TICKET_PRIORITY_IDS.map((id) => priorityById[id]),
    [priorityById]
  );

  return {
    priorities,
    getPriorityById,
  };
};
